import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const hisingenPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-hisingen",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Hisingen – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling på Hisingen. Vi hämtar möbler, vitvaror och skräp i Lundby, Torslanda, Backa, Lindholmen, Biskopsgården och Kärra. Fast pris, ofta samma dag.",
      h1: "Bortforsling Hisingen",
    },

    hero: {
      heading: "Bortforsling på Hisingen",
      intro:
        "Boka bortforsling på Hisingen – vi hämtar möbler, vitvaror och skrymmande föremål över hela ön, från de nya lägenheterna vid Lindholmen, Eriksberg och Sannegården till villorna i Torslanda, Björlanda och Kärra. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling på Hisingen – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar på Hisingen",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling på Hisingen och körs till Bulycke återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Torslanda, Hisingen",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Lundby, Hisingen",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Lindholmen, Hisingen",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Backa, Hisingen",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Biskopsgården, Hisingen",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling på Hisingen – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Hisingen är Sveriges fjärde största ö och utgör norra delen av Göteborg, omfluten av Göta älv och uppdelad i stadsområdena Lundby, Norra Hisingen och Västra Hisingen. Bebyggelsen är otroligt varierad: de nya bostadskvarteren på de gamla varvsområdena vid Lindholmen, Eriksberg och Sannegården, miljonprogrammets hus i Biskopsgården, Länsmansgården och Backa, samt de havsnära villorna i Torslanda, Björlanda och Kärra. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, medan det i villan snarare handlar om garage, förråd och stora tomter. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Hisingen har en stor fördel här – två av Göteborgs återvinningscentraler ligger på ön: Bulycke i Västra Hisingen och Tagene i Norra Hisingen. Det betyder att avfallet stannar lokalt och inte behöver köras över älven. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att vi planerar rutten med hänsyn till broarna och tunnlarna – Hisingsbron, Älvsborgsbron, Tingstadstunneln och Lundbytunneln – tar vi oss snabbt mellan öns stadsdelar. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både höghusen i Biskopsgården och de smala villagatorna i Torslanda – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling på Hisingen",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Torslanda, Björlanda, Kärra eller Tuve är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet vid Lindholmen, Eriksberg, Backa eller Biskopsgården, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som göteborgare på Bulycke eller Tagene återvinningscentral.",
      additionalSections: [
        {
          heading: "Stadsdelar och områden vi täcker på Hisingen",
          content:
            "Vi utför bortforsling över hela Hisingen – från de nya vattennära kvarteren till miljonprogrammet och villaområdena. Oavsett om du bor i en nybyggd lägenhet på Lindholmen, ett radhus i Kärra, en villa i Torslanda eller ett hus i Brämaregården kan vi vara på plats, ofta redan samma dag.\n\nI Lundby – med Lindholmen, Eriksberg, Sannegården, Kvillebäcken, Brämaregården och Wieselgrensplatsen – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, både i de nybyggda kvarteren på de gamla varvsområdena och i den äldre landshövdingehusbebyggelsen. I Västra Hisingen möter vi Biskopsgården och Länsmansgården samt de växande villaområdena i Torslanda, Amhult och Björlanda nära havet.\n\nVi är minst lika snabba i Norra Hisingen: Backa och Backaplan, Kärra, Skogome, Tuve, Säve och Brunnsbo. Tack vare närheten till Lundbyleden, Hisingsleden och öns broar och tunnlar planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon stadsdel.",
        },
        {
          heading: "Återvinning och miljöansvar på Hisingen",
          content:
            "En viktig del av vår bortforsling på Hisingen är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. I Göteborg sköts avfallet av Kretslopp och vatten, och Hisingen har den stora fördelen att två av stadens stationer ligger på ön:\n\nBulycke återvinningscentral i Västra Hisingen – ombyggd med stort fokus på återbruk, där bland annat Human Bridge, Stadsmissionen och Emmaus Björkå tar emot föremål för återanvändning, utöver sorterat grovavfall, elavfall och farligt avfall. Tagene återvinningscentral i Norra Hisingen – för uppdrag i de norra delarna. Eftersom båda ligger på Hisingen behöver avfallet inte köras över älven, vilket håller restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling på Hisingen med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Hisingens stadsdelar, från de nya kvarteren på Lindholmen och Eriksberg till villatomterna i Torslanda och höghusen i Biskopsgården, och vet att Bulycke och Tagene återvinningscentral ligger på ön. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och når Hisingen via broarna och tunnlarna. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för att vi kör över älven, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Bulycke och Tagene återvinningscentral. Det som går att donera skänks vidare till Stadsmissionen, Emmaus och Human Bridge. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling på Hisingen?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon stadsdel på Hisingen.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling på Hisingen?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling på Hisingen?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och når Hisingen via Hisingsbron, Älvsborgsbron och Tingstadstunneln, så vi tar oss snabbt mellan stadsdelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling på Hisingen?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa på Hisingen?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Lindholmen, Eriksberg, Backa och Biskopsgården. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage på Hisingen?",
        answer:
          "Ja, en stor del av våra uppdrag på Hisingen är villahämtningar i Torslanda, Björlanda, Kärra och Tuve. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen på Hisingen försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling på Hisingen?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Hisingen har två stationer – Bulycke i väster och Tagene i norr – så avfallet stannar på ön och behöver inte köras över älven. Det som kan återanvändas skänks till Stadsmissionen, Emmaus eller Human Bridge.",
      },
      {
        question: "Kan jag boka bortforsling på Hisingen på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd på Hisingen?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Torslanda och de gemensamma förrådsutrymmena i lägenhetshusen på Lindholmen och i Backa.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling på Hisingen?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet på Lindholmen och ditt förråd på en annan adress på Hisingen.",
      },
      {
        question: "Vilka stadsdelar på Hisingen täcker ni?",
        answer:
          "Vi utför bortforsling över hela Hisingen – Lundby (Lindholmen, Eriksberg, Sannegården, Kvillebäcken, Brämaregården), Västra Hisingen (Biskopsgården, Länsmansgården, Torslanda, Amhult, Björlanda) och Norra Hisingen (Backa, Kärra, Tuve, Säve, Skogome). Inga extra avgifter för någon stadsdel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling på Hisingen?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som göteborgare på Bulycke eller Tagene återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Hisingen utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling på Hisingen i 3 enkla steg",
      step2Text:
        "Våra ungdomar kommer till dig på Hisingen och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling på Hisingen idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling över hela Hisingen.",
    },

    en: {
      slug: "junk-removal-hisingen",
      meta: {
        title: "Junk Removal Hisingen – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal on Hisingen. We pick up furniture, appliances and bulky items in Lundby, Torslanda, Backa, Lindholmen, Biskopsgården and Kärra. Fixed price, often same day.",
        h1: "Junk Removal Hisingen",
      },
      hero: {
        heading: "Junk Removal on Hisingen",
        intro:
          "Book junk removal on Hisingen – we collect furniture, appliances and bulky items across the whole island, from the new apartments at Lindholmen, Eriksberg and Sannegården to the villas in Torslanda, Björlanda and Kärra. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal on Hisingen – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove on Hisingen",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal on Hisingen and driven to Bulycke recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Torslanda, Hisingen",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Lundby, Hisingen",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Lindholmen, Hisingen",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Backa, Hisingen",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Biskopsgården, Hisingen",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal on Hisingen – fast, hassle-free and responsible",
        mainText:
          "Hisingen is Sweden's fourth largest island and forms the northern part of Gothenburg, surrounded by the Göta älv river and divided into the city areas of Lundby, Norra Hisingen and Västra Hisingen. The housing is incredibly varied: the new residential quarters on the old shipyard sites at Lindholmen, Eriksberg and Sannegården, the 'miljonprogram' buildings of Biskopsgården, Länsmansgården and Backa, and the seaside villas of Torslanda, Björlanda and Kärra. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, while in a villa it is more about garages, storage rooms and large plots. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Hisingen has a big advantage here – two of Gothenburg's recycling centres are on the island: Bulycke in Västra Hisingen and Tagene in Norra Hisingen. This means the waste stays local and doesn't have to be driven across the river. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause we plan the route with the bridges and tunnels in mind – Hisingsbron, the Älvsborg bridge, the Tingstad tunnel and the Lundby tunnel – we move quickly between the island's districts. This means we can often be on site the same day you book. Our workers are used to both the high-rises of Biskopsgården and the narrow villa streets of Torslanda – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal on Hisingen",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Torslanda, Björlanda, Kärra or Tuve, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment at Lindholmen, Eriksberg, Backa or Biskopsgården, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Gothenburg resident you can drop those off for free at the Bulycke or Tagene recycling centre.",
        additionalSections: [
          {
            heading: "Districts and areas we cover on Hisingen",
            content:
              "We perform junk removal across the whole of Hisingen – from the new waterfront quarters to the 'miljonprogram' and the villa areas. Whether you live in a new apartment at Lindholmen, a townhouse in Kärra, a villa in Torslanda or a house in Brämaregården, we can be on site, often the same day.\n\nIn Lundby – with Lindholmen, Eriksberg, Sannegården, Kvillebäcken, Brämaregården and Wieselgrensplatsen – we are highly experienced with apartment pickups via elevator and stairwell, both in the newly built quarters on the old shipyard sites and in the older landshövdingehus housing. In Västra Hisingen we meet Biskopsgården and Länsmansgården as well as the growing villa areas of Torslanda, Amhult and Björlanda near the sea.\n\nWe are just as quick in Norra Hisingen: Backa and Backaplan, Kärra, Skogome, Tuve, Säve and Brunnsbo. Thanks to the proximity of Lundbyleden, Hisingsleden and the island's bridges and tunnels, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility on Hisingen",
            content:
              "An important part of our junk removal on Hisingen is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. In Gothenburg, waste is handled by Kretslopp och vatten, and Hisingen has the big advantage that two of the city's stations are on the island:\n\nBulycke recycling centre in Västra Hisingen – rebuilt with a strong focus on reuse, where Human Bridge, Stadsmissionen and Emmaus Björkå receive items for reuse, in addition to sorted bulky waste, e-waste and hazardous waste. Tagene recycling centre in Norra Hisingen – for jobs in the northern parts. Because both are on Hisingen, the waste doesn't have to be driven across the river, which keeps travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal on Hisingen with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Hisingen's districts, from the new quarters at Lindholmen and Eriksberg to the villa plots of Torslanda and the high-rises of Biskopsgården, and that the Bulycke and Tagene recycling centres are on the island. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and reach Hisingen via the bridges and tunnels. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharge for crossing the river, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via the Bulycke and Tagene recycling centres. Items suitable for donation are passed on to Stadsmissionen, Emmaus and Human Bridge. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal on Hisingen cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district on Hisingen.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal on Hisingen?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal on Hisingen?",
          answer:
            "We have vans and staff across the Gothenburg region and reach Hisingen via the Hisingsbron, the Älvsborg bridge and the Tingstad tunnel, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal on Hisingen?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs on Hisingen?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Lindholmen, Eriksberg, Backa and Biskopsgården. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages on Hisingen?",
          answer:
            "Yes, a large share of our jobs on Hisingen are villa pickups in Torslanda, Björlanda, Kärra and Tuve. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal on Hisingen insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal on Hisingen?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Hisingen has two stations – Bulycke in the west and Tagene in the north – so the waste stays on the island and doesn't have to be driven across the river. Items that can be reused are donated to Stadsmissionen, Emmaus or Human Bridge.",
        },
        {
          question: "Can I book junk removal on Hisingen on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units on Hisingen?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the villa storage rooms in Torslanda and the shared storage rooms in the apartment buildings at Lindholmen and in Backa.",
        },
        {
          question: "Can you make multiple stops during junk removal on Hisingen?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment at Lindholmen and a storage unit at another address on Hisingen, for example.",
        },
        {
          question: "Which districts on Hisingen do you cover?",
          answer:
            "We perform junk removal across all of Hisingen – Lundby (Lindholmen, Eriksberg, Sannegården, Kvillebäcken, Brämaregården), Västra Hisingen (Biskopsgården, Länsmansgården, Torslanda, Amhult, Björlanda) and Norra Hisingen (Backa, Kärra, Tuve, Säve, Skogome). No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal on Hisingen?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Gothenburg resident you can drop those off for free at the Bulycke or Tagene recycling centre.",
        },
        {
          question: "Which municipalities around Hisingen do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal on Hisingen in 3 easy steps",
        step2Text:
          "Our young workers come to you on Hisingen and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal on Hisingen today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole of Hisingen.",
      },
    },
  },
];
